import { FC, useState } from 'react';
import {
  DataRoomAccessPermission,
  DataRoomAccessStatus,
  DataRoomRecipientKind,
  DataRoomStatus,
} from 'shared';
import { twMerge } from 'tailwind-merge';

import {
  CheckCircleIcon,
  ChevronDownIcon,
  InfoCircle,
  PlusIcon,
  SlashCircleIcon,
  XCloseIcon,
} from '../../../../../assets/icons/index';
import { PlusIconVariant } from '../../../../../assets/icons/plus-icon';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import Loader from '../../../../../components/loader/index';
import { ModalWrapper } from '../../../../../components/modal-wrapper/index';
import { PaginationController } from '../../../../../components/pagination-controller/index';
import { Search } from '../../../../../components/search/index';
import {
  Select,
  SelectButton,
  SelectOption,
  SelectOptions,
} from '../../../../../components/select/index';
import { Tag, TagVariant } from '../../../../../components/tag/index';
import {
  DataRoomAccessAction,
  DataRoomAccessRow,
  useAccessAction,
  useDataRoom,
  useDataRoomAccess,
  usePatchDataRoom,
  useSaveAccessPermissions,
} from '../../../../../hooks/space/index';
import { StringKey } from '../../../../../lang/index';
import { StakeholderType, stakeholderTypeTitle } from '../../../../../types/stakeholder.types';
import { DataRoomModalType } from '../../../enums/data-room-modal-type.enum';
import { DataRoomModalStore } from '../../../store/data-room-modal.store';
import { AccessActionModal } from './access-action-modal';
import { AccessEmptyState } from './access-empty-state';
import { AccessTable } from './access-table';
import { InvitePeopleModal } from './invite-people-modal';

/**
 * Six, which is what the modal is drawn around: a 44px header, six 72px rows and a 68px pager come
 * to the 544 the design gives the table, and that is the only reason its dialog is 790 tall. A
 * larger page would make the modal scroll inside itself, which is what the mock is shaped to avoid.
 * The story itself says nothing about paging.
 */
const PAGE_SIZE = 6;

const ACCESS_FILTER: { label: StringKey; value: '' | DataRoomAccessStatus }[] = [
  { label: 'ALL_ACCESS', value: '' },
  { label: 'ACTIVE', value: DataRoomAccessStatus.ACTIVE },
  { label: 'PENDING', value: DataRoomAccessStatus.PENDING },
  { label: 'REVOKED', value: DataRoomAccessStatus.REVOKED },
];

const STAKEHOLDER_FILTER: { label: StringKey; value: '' | DataRoomRecipientKind }[] = [
  { label: 'ALL_STAKEHOLDERS', value: '' },
  { label: 'INTERNAL', value: DataRoomRecipientKind.INTERNAL },
  { label: 'EXTERNAL', value: DataRoomRecipientKind.EXTERNAL },
];

const TYPE_FILTER: { label: StringKey; value: '' | StakeholderType }[] = [
  { label: 'ALL_TYPES', value: '' },
  ...Object.values(StakeholderType).map((type) => ({
    label: stakeholderTypeTitle[type],
    value: type,
  })),
];

const FilterSelect = <T extends string>({
  onChange,
  options,
  value,
}: {
  onChange: (value: T) => void;
  options: { label: StringKey; value: T }[];
  value: T;
}) => {
  const selected = options.find((option) => option.value === value) ?? options[0];

  return (
    <Select<T> onChange={onChange} value={value}>
      {({ open }: { open: boolean }) => (
        <div className="relative">
          <SelectButton className="flex h-9 items-center gap-2 rounded border border-gray-100 bg-white py-[6px] pl-3 pr-[6px] shadow-xs">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id={selected.label} />
            </span>
            <span className="flex size-5 shrink-0 items-center justify-center">
              <ChevronDownIcon
                className={twMerge('size-[13px] transition-transform', open && 'rotate-180')}
              />
            </span>
          </SelectButton>
          <SelectOptions
            anchor={{ gap: 4, to: 'bottom end' }}
            className="pointer-events-auto z-[60] mt-0 max-h-[216px] min-w-40 overflow-y-auto rounded border border-gray-100 bg-white shadow-lg"
            portal
          >
            {options.map((option) => (
              <SelectOption key={option.value || 'all'} value={option.value}>
                <span className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  <AppFormattedMessage id={option.label} />
                </span>
              </SelectOption>
            ))}
          </SelectOptions>
        </div>
      )}
    </Select>
  );
};

export const ManageAccessModal: FC = () => {
  const isOpen = DataRoomModalStore(({ isOpen }) => isOpen);
  const type = DataRoomModalStore(({ type }) => type);
  const dataRoomId = DataRoomModalStore(({ metadata }) => metadata?.spaceId);
  const handleDismiss = DataRoomModalStore(({ handleDismiss }) => handleDismiss);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Invite opens on top of this list rather than in place of it, so it is local state here and not
  // another entry in the modal store, which holds one modal at a time.
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'' | DataRoomAccessStatus>('');
  const [kindFilter, setKindFilter] = useState<'' | DataRoomRecipientKind>('');
  const [typeFilter, setTypeFilter] = useState<'' | StakeholderType>('');
  const [page, setPage] = useState(1);
  // Permission edits are held here until Save; row actions never come through this map.
  const [pendingPermissions, setPendingPermissions] = useState<
    Record<string, DataRoomAccessPermission>
  >({});
  // The row action awaiting confirmation. Kept local rather than pushed through the modal store:
  // the store holds one modal at a time, so opening a confirmation there would close this one.
  const [pendingAction, setPendingAction] = useState<{
    action: DataRoomAccessAction;
    row: DataRoomAccessRow;
  } | null>(null);

  const { dataRoom } = useDataRoom(dataRoomId);
  const { applyAccessAction, isPending: isApplyingAction } = useAccessAction();
  const { isPending: isSaving, savePermissions } = useSaveAccessPermissions();
  const { isPending: isReactivating, patchDataRoom } = usePatchDataRoom();

  const { isEditable, isError, isLoading, records, totalPages } = useDataRoomAccess(dataRoomId, {
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(kindFilter ? { recipientKind: kindFilter } : {}),
    ...(typeFilter ? { type: typeFilter } : {}),
    skip: (page - 1) * PAGE_SIZE,
    take: PAGE_SIZE,
  });

  if (type !== DataRoomModalType.MANAGE_ACCESS || !dataRoomId) {
    return null;
  }

  const hasFilters = Boolean(debouncedSearch || statusFilter || kindFilter || typeFilter);
  const isRoomActive = dataRoom?.status === DataRoomStatus.ACTIVE;
  const changes = Object.entries(pendingPermissions).map(([accessId, permission]) => ({
    accessId,
    permission,
  }));

  const permissionFor = (row: DataRoomAccessRow) => pendingPermissions[row.id] ?? row.permission;

  const changePermission = (row: DataRoomAccessRow, permission: DataRoomAccessPermission) =>
    setPendingPermissions((current) => {
      // Setting it back to the stored value is not a change; dropping the entry keeps Save
      // disabled when the person has undone their own edit.
      if (permission === row.permission) {
        const { [row.id]: _removed, ...rest } = current;
        return rest;
      }

      return { ...current, [row.id]: permission };
    });

  // The confirmation stays up, with its button spinning, until the request lands: closing it on
  // the click would claim the revoke happened before anything had answered for it.
  const confirmAction = () => {
    if (!pendingAction || isApplyingAction) return;

    applyAccessAction(
      {
        accessId: pendingAction.row.id,
        action: pendingAction.action,
        id: dataRoomId,
        status: pendingAction.row.status,
      },
      { onSuccess: () => setPendingAction(null) },
    );
  };

  const openInvite = () => setIsInviteOpen(true);

  const save = () => {
    if (!changes.length || isSaving) return;

    savePermissions({ changes, id: dataRoomId }, { onSuccess: () => setPendingPermissions({}) });
  };

  const isEmpty = !isLoading && !isError && records.length === 0 && !hasFilters;

  return (
    <ModalWrapper
      // The empty state is drawn as a square panel; the table needs the room its eight columns ask
      // for. One modal, two widths, because they are two designs.
      className={twMerge('w-full shadow-none', isEmpty ? 'max-w-[600px]' : 'max-w-[1183px]')}
      control={{ onOpenChange: (open) => !open && handleDismiss(), open: isOpen }}
      onBgClick={handleDismiss}
      side="CENTER"
    >
      <div
        className={twMerge(
          'flex max-h-[90vh] w-full flex-col overflow-hidden rounded bg-white p-4 shadow-[0px_32px_36px_0px_rgba(16,24,40,0.18)]',
          isEmpty ? 'h-[600px] gap-4' : 'gap-6',
        )}
      >
        <div className="flex min-h-0 w-full flex-1 flex-col gap-4">
          <div className="flex w-full flex-col border-b border-gray-100 pb-4">
            <div className="flex w-full items-start justify-end gap-2 py-1">
              <div className="flex min-w-px flex-1 flex-col">
                <p className="text-xl font-semibold leading-8 text-gray-700">
                  <AppFormattedMessage id="MANAGE_ACCESS_TITLE" />
                </p>
                <div className="flex items-center gap-2">
                  <span className="truncate text-xs font-[450] leading-4 text-gray-500">
                    {dataRoom?.name ?? ''}
                  </span>
                  <span className="h-[9px] w-px bg-gray-200" />
                  {/* Inactive carries its own mark, not a bare word: the slashed circle is what
                      the paused room is drawn with everywhere else it appears. */}
                  <Tag
                    icon={
                      isRoomActive ? (
                        <CheckCircleIcon className="size-3" iconColor="#039855" />
                      ) : (
                        <SlashCircleIcon className="size-3" iconColor="#344054" />
                      )
                    }
                    title={<AppFormattedMessage id={isRoomActive ? 'ACTIVE' : 'INACTIVE'} />}
                    variant={isRoomActive ? TagVariant.GREEN : TagVariant.GRAY}
                  />
                </div>
              </div>
              {isEmpty && (
                <Button className="size-[21px] w-fit p-0" onClick={handleDismiss} styleType="NONE">
                  <XCloseIcon className="size-[21px]" iconColor="#344054" />
                </Button>
              )}
            </div>
          </div>

          {/* Not just greyed controls: an Admin needs to know the room status is why, and that
              nobody lost their place in the list. */}
          {!isEditable && !isLoading && !isError && (
            <div className="flex items-center gap-2 rounded-md bg-sunbeam-50 px-4 py-3">
              <InfoCircle className="size-4 shrink-0" iconColor="#DC6803" />
              <p className="text-xs font-[450] leading-4 text-gray-800">
                <AppFormattedMessage id="DATA_ROOM_INACTIVE_ACCESS_PAUSED" />
              </p>
              <button
                className="shrink-0 text-xs font-[450] leading-4 text-brand-800 underline disabled:opacity-50"
                data-permission-required
                disabled={isReactivating}
                onClick={() => patchDataRoom({ id: dataRoomId, status: DataRoomStatus.ACTIVE })}
                type="button"
              >
                <AppFormattedMessage id="REACTIVATE" />
              </button>
            </div>
          )}

          {!isEmpty && (
            <div className="flex w-full items-center justify-between">
              <span className="text-[10px] font-semibold uppercase leading-4 tracking-[1px] text-gray-500">
                <AppFormattedMessage id="PEOPLE_WITH_ACCESS" />
              </span>
              <div className="flex items-center gap-3">
                {/* The toolbar's own search, the one the workspaces header already uses: it folds
                    to a 36px icon and carries its own debounce. */}
                <Search
                  buttonClassName="bg-white"
                  callback={(value) => {
                    setDebouncedSearch(value);
                    setPage(1);
                  }}
                />
                <FilterSelect
                  onChange={(value) => {
                    setStatusFilter(value);
                    setPage(1);
                  }}
                  options={ACCESS_FILTER}
                  value={statusFilter}
                />
                <FilterSelect
                  onChange={(value) => {
                    setTypeFilter(value);
                    setPage(1);
                  }}
                  options={TYPE_FILTER}
                  value={typeFilter}
                />
                <FilterSelect
                  onChange={(value) => {
                    setKindFilter(value);
                    setPage(1);
                  }}
                  options={STAKEHOLDER_FILTER}
                  value={kindFilter}
                />
                {/* A paused room offers nothing to act on, so the toolbar ends at the filters
                    rather than carrying a button that could never be pressed. */}
                {isEditable && (
                  <Button
                    className="h-9 w-fit gap-1 py-1.5 pl-4 pr-5"
                    onClick={openInvite}
                    type="button"
                  >
                    <PlusIcon
                      className="size-5"
                      iconColor="#ffffff"
                      variant={PlusIconVariant.VAULT}
                    />
                    <AppFormattedMessage id="INVITE_PEOPLE" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Vertical only: the table divides the width it is given rather than insisting on a
              total, so there is never anything to scroll sideways to. */}
          <div className="flex min-h-0 flex-1 flex-col items-start gap-4 overflow-y-auto">
            {isLoading && <Loader />}
            {!isLoading && isError && (
              <p className="text-sm font-[450] text-fireside-500">
                <AppFormattedMessage id="ACCESS_LOAD_FAILED" />
              </p>
            )}
            {isEmpty && <AccessEmptyState isEditable={isEditable} onInvite={openInvite} />}
            {!isLoading && !isError && records.length === 0 && hasFilters && (
              <p className="text-sm font-[450] text-gray-500">
                <AppFormattedMessage id="NO_PEOPLE_MATCH_FILTERS" />
              </p>
            )}
            {!isLoading && !isError && records.length > 0 && (
              <AccessTable
                footer={
                  totalPages > 1 && (
                    <div className="flex w-full justify-center">
                      <PaginationController
                        currentPage={page}
                        onClick={setPage}
                        totalPages={totalPages}
                        wrapperClassName="w-fit justify-center pb-3"
                      />
                    </div>
                  )
                }
                isEditable={isEditable}
                onAction={(row, action) => setPendingAction({ action, row })}
                onPermissionChange={changePermission}
                permissionFor={permissionFor}
                records={records}
              />
            )}
          </div>
        </div>

        {/* Nothing to cancel or save before anybody has access: the empty state ends at its one
            call to action, and the cross in the header is the way out. */}
        {!isEmpty && (
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              className="h-11 w-fit border border-gray-300 px-6 text-sm font-[450] text-gray-700"
              onClick={handleDismiss}
              styleType="DEFAULT_ROUNDED"
            >
              <AppFormattedMessage id="CANCEL" />
            </Button>
            <Button
              className="h-11 w-fit px-6"
              disabled={!changes.length || isSaving || !isEditable}
              isLoading={isSaving}
              onClick={save}
            >
              <AppFormattedMessage id="SAVE" />
            </Button>
          </div>
        )}
      </div>

      {isInviteOpen && (
        <InvitePeopleModal dataRoomId={dataRoomId} onClose={() => setIsInviteOpen(false)} />
      )}

      {/* Row actions take effect on confirmation, never on Save — each asks in its own dialog
          over this list, the way deleting a data room does. */}
      {pendingAction && (
        <AccessActionModal
          action={pendingAction.action}
          isPending={isApplyingAction}
          onCancel={() => setPendingAction(null)}
          onConfirm={confirmAction}
          roomName={dataRoom?.name ?? ''}
          row={pendingAction.row}
        />
      )}
    </ModalWrapper>
  );
};
